/* eslint-disable react/require-default-props */

import { ReactNode, forwardRef, memo, useMemo } from "react"

import createStyle, { CustomTagArgs } from "@josephmark/createstyle"
import clsx from "clsx"

import { Link } from "@/components/Link"
import { H5, LabelSmall, P2 } from "@/components/TypeRamp"

// {
//   style: { boxShadow: "inset -1px -1px rgba(0,0,0,0.2), 0 1px 4px white" },
// }
const themes = {
  orange: clsx("bg-orange rounded-lg text-white disabled:opacity-50"),
  primary: clsx(
    "hover:bg-concrete text-slate disabled:text-opacity-40 border-empress/20 disabled:bg-concrete rounded-lg border bg-white dark:disabled:bg-black"
  ),
  secondary: clsx(
    "bg-charcoal disabled:hover:bg-slate disabled:bg-empress dark:border-empress/20 border-charcoal hover:bg-slate rounded-lg border text-white disabled:text-white/60"
  ),
  tertiary: clsx(
    "border-scorpion/20 hover:border-scorpion/60 disabled:bg-scorpion/20 rounded-lg border bg-transparent hover:bg-black/5 disabled:text-current/40 dark:border-white/20 dark:hover:border-white/60"
  ),
  grey: clsx(
    "rounded-lg bg-black/5 text-black hover:bg-black/10 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
  ),
  destructive: clsx("rounded-lg bg-red-600 text-white hover:bg-red-400"),
  custom: "",
}

const sizes = {
  xlarge: clsx(P2.classNames, "px-5 py-3"),
  large: clsx(H5.classNames, "px-3 py-2.5"),
  small: clsx(LabelSmall.classNames, "px-3 py-2.5 font-normal"),
  xsmall: clsx(LabelSmall.classNames, "px-1.5 py-0.5"),
  custom: "",
}

type ButtonWithLabel = {
  label: string
}

type ButtonWithChildren = {
  children: ReactNode
}

type BaseButtonProps = {
  /* eslint-disable react/no-unused-prop-types */
  theme?: keyof typeof themes
  size?: keyof typeof sizes
  /* eslint-enable react/no-unused-prop-types */
} & Omit<CustomTagArgs<HTMLElement>, "size">

// Enforces either label or children on a button
export type ButtonProps = (ButtonWithLabel | ButtonWithChildren) & BaseButtonProps

const ButtonBase = createStyle(
  "a",
  "group relative inline-flex cursor-pointer items-center justify-center gap-1.5 text-center transition-all disabled:cursor-not-allowed"
)

/**
 * Button component for rendering styled buttons or links with various themes and sizes.
 * Supports both label and children content, and can render as a link or button based on the href prop.
 *
 * @param {ButtonProps} props - The props for the Button component.
 * @returns {JSX.Element} The rendered button or link.
 */
export const Button = memo(
  forwardRef<HTMLAnchorElement, ButtonProps>(
    ({ href, className = "", label, theme = "primary", size = "large", children, ...props }, ref) => {
      const mergedCn = clsx(className, themes[theme], sizes[size])

      const contents = useMemo(() => {
        return label || children
      }, [children, label])

      if (href?.slice(0, 1) === "/" || href?.slice(0, 1) === "#") {
        return (
          <Link ref={ref} href={href} className={clsx(ButtonBase.classNames, mergedCn)} {...props}>
            {contents}
          </Link>
        )
      }
      return (
        <ButtonBase ref={ref} href={href} className={mergedCn} {...props}>
          {contents}
        </ButtonBase>
      )
    }
  )
)
